import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import {
  $transferReasonsStore,
  // $transferRequestsFeeStore,
  $transferRequestsGetFxRateStore,
} from '@lib/store';
import { AmountUtils, cn, CurrencyUtils, PaletteUtils } from '@shared/utils';
import type { AccountResponse } from '@shared/api/rest/accounts';
import { IFormDataOwnAccount, IFormDataBusinessBetween, IFormDataIndividualBetween } from '@pages/paymentBetweenAccounts/widgets';
import { IFormDataSettlementCard } from '@pages/paymentCardSettlement';
import { IFormDataBusinessOther, IFormDataIndividualOther } from '@pages/paymentOtherBank/widgets';
import { IFormDataBusinessCreate, IFormDataIndividualCreate } from '@pages/recipientEditor/widgets';
import { BaseInput, ErrorMessage, ExchangeRate, Radio, Select, Text } from '@shared/widgets';
import { AmountTypeEnum } from '@shared/api/rest/payment';
import { useDebounce } from '@shared/hooks';

// export enum FastPaymentCountryEnum {
//   US = 'US',
// }

// export enum FastPaymentCurrencyEnum {
//   USD = 'USD',
// }

export type PaymentFormDataType =
  | IFormDataIndividualCreate
  | IFormDataBusinessCreate
  | IFormDataBusinessBetween
  | IFormDataBusinessOther
  | IFormDataIndividualBetween
  | IFormDataIndividualOther
  | IFormDataOwnAccount
  | IFormDataSettlementCard;

interface PropsType {
  className?: string;
  account?: AccountResponse | null;
  accountTo?: AccountResponse | null;
  currency?: string;
  reasonOptions: { label: string; value: string }[];
  onRequestFee: () => Promise<void>;
}

export const PaymentFormBlock = (props: PropsType) => {
  const transferReasonsStore = useUnit($transferReasonsStore.store);
  const fxRateStore = useUnit($transferRequestsGetFxRateStore.store);
  // const feeStore = useUnit($transferRequestsFeeStore.store);

  const isExchangeRateChanged = useRef<boolean>(false);

  const { control, watch, setValue } = useFormContext<PaymentFormDataType>();
  const amountType = watch('amountType');
  const amountVal = watch('amount');
  const beneficiaryAmountVal = watch('beneficiaryAmount');
  const currency = watch('currency');
  const bankCountry = watch('bankCountry');
  const recipient = watch('recipient');

  const { t } = useTranslation('payments');

  useEffect(() => {
    setValue('fastPayment', true);
  }, [currency, bankCountry, recipient, props.account]);

  const debouncedAmount = useDebounce(amountVal, 400);
  const debouncedBeneficiaryAmount = useDebounce(beneficiaryAmountVal, 400);
  const debouncedFxRate = useDebounce(fxRateStore.data, 400);
  const updateAmounts = useCallback(
    (fxRate?: number) => {
      const rate = fxRate ?? fxRateStore.data;
      if (!rate) return;

      if (amountType === AmountTypeEnum.SenderAmount) {
        if (!debouncedAmount || debouncedAmount <= 0) return;
        const newBeneficiaryAmount = +(debouncedAmount * rate).toFixed(2);
        setValue('beneficiaryAmount', newBeneficiaryAmount, { shouldValidate: true });
      } else if (amountType === AmountTypeEnum.BeneficiaryAmount) {
        if (!debouncedBeneficiaryAmount || debouncedBeneficiaryAmount <= 0) return;
        const newAmount = +(debouncedBeneficiaryAmount / rate).toFixed(2);
        setValue('amount', newAmount, { shouldValidate: true });
      }
    },
    [fxRateStore.data, amountType, debouncedAmount, debouncedBeneficiaryAmount, setValue],
  );
  useEffect(() => {
    if (fxRateStore.data) isExchangeRateChanged.current = true;
  }, [fxRateStore.data]);
  useEffect(() => {
    if (!debouncedFxRate) return;

    if (isExchangeRateChanged.current) {
      isExchangeRateChanged.current = false;
      updateAmounts(debouncedFxRate);
      return;
    }

    updateAmounts(debouncedFxRate);
  }, [debouncedAmount, debouncedBeneficiaryAmount, amountType, debouncedFxRate, updateAmounts]);

  const amountTypeOptions = useMemo(
    () => Object.entries(AmountTypeEnum).map(([value, label]) => ({ text: t(`form.placeholders.${label}`), value })),
    [t],
  );
  const hasRecipient = !!(props.accountTo || recipient || (currency && bankCountry));
  return (
    <div className={cn('flex flex-col gap-2', props.className)}>
      <Text className={cn(PaletteUtils.isVcfcPalette && 'dark:text-white/50')}>{t('form.titles.payment')}</Text>
      <Controller
        name='amountType'
        control={control}
        render={({ field: { value, onChange } }) => <Radio orientation='horizontal' options={amountTypeOptions} value={value} onChange={onChange} />}
      />
      <div className='flex flex-col gap-2'>
        <Controller
          name='amount'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            min: { value: 0.01, message: t('form.errorMessages.minAmount') },
          }}
          render={({ field, fieldState: { error } }) => (
            <div className={cn('order-1', amountType === AmountTypeEnum.BeneficiaryAmount && 'order-2')}>
              <div className='relative'>
                <BaseInput
                  {...field}
                  label={t('form.placeholders.amount')}
                  type='number'
                  placeholder={t('form.placeholders.amount')}
                  required
                  onlyPositive
                  readOnly={amountType !== AmountTypeEnum.SenderAmount}
                  prefix={CurrencyUtils.getSymbolByCode(props.account?.currency)}
                  disabled={!hasRecipient}
                  value={(field.value as unknown as string) ?? undefined}
                  onChange={(value) => {
                    if (typeof value !== 'string') return;
                    if (value.length === 0) setValue('beneficiaryAmount', '' as unknown as undefined);
                    if (value.length === 10) return;

                    const correctedValue = value.replace(/^(\d+)(\.\d{0,2})?\d*$/, (_, p1, p2) => {
                      if (p2) return p1 + p2.padEnd(3, '0').slice(0, 3);
                      else return p1;
                    });
                    if (correctedValue !== '') {
                      const floatValue = parseFloat(correctedValue.replace(',', '.'));
                      field.onChange(floatValue);
                    } else field.onChange(undefined);
                  }}
                  onBlur={props.onRequestFee}
                />
                {/* {field.value! > 0 && (
                  <span
                    className={cn(
                      'absolute bottom-[20px] left-[120px] translate-y-1/2 text-sm font-light opacity-50',
                      PaletteUtils.isVcfcPalette && 'bottom-[25px]',
                    )}
                  >
                    {feeStore.data !== undefined && feeStore.data !== null && !feeStore.loading
                      ? `+ ${AmountUtils.transformToAmountWithCurrency(feeStore.data || 0)} ${t('form.fields.fee')}`
                      : t('form.fields.feeCalculating')}
                  </span>
                )} */}
              </div>
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='beneficiaryAmount'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            min: { value: 0.01, message: t('form.errorMessages.minAmount') },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <div className={cn('order-2', amountType === AmountTypeEnum.BeneficiaryAmount && 'order-1')}>
              <div className='relative'>
                <BaseInput
                  label={t('form.placeholders.beneficiaryAmount')}
                  type='number'
                  placeholder={t('form.placeholders.beneficiaryAmount')}
                  required
                  onlyPositive
                  readOnly={amountType !== AmountTypeEnum.BeneficiaryAmount}
                  prefix={CurrencyUtils.getSymbolByCode(props.accountTo?.currency || recipient?.currency || props.currency)}
                  disabled={!hasRecipient}
                  value={(value as unknown as string) ?? undefined}
                  onChange={(value) => {
                    if (typeof value !== 'string') return;
                    if (value.length === 0) setValue('amount', '' as unknown as undefined);
                    if (value.length === 10) return;

                    const correctedValue = value.replace(/^(\d+)(\.\d{0,2})?\d*$/, (_, p1, p2) => {
                      if (p2) return p1 + p2.padEnd(3, '0').slice(0, 3);
                      else return p1;
                    });
                    if (correctedValue !== '') {
                      const floatValue = parseFloat(correctedValue.replace(',', '.'));
                      onChange(floatValue);
                    } else onChange(undefined);
                  }}
                  onBlur={props.onRequestFee}
                />
              </div>
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
      </div>
      <Controller
        name='paymentReason'
        control={control}
        rules={{
          required: t('errors.form.required', { ns: 'common' }),
          maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
        }}
        render={({ field, fieldState: { error } }) => (
          <div>
            <BaseInput
              {...field}
              label={t('form.placeholders.transactionReference')}
              placeholder={t('form.placeholders.transactionReference')}
              required
            />
            <ErrorMessage text={error?.message} />
          </div>
        )}
      />
      <Controller
        name='purpose'
        control={control}
        rules={{ required: t('errors.form.required', { ns: 'common' }) }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <div>
            <Select
              buttonClassName='bg-transparent'
              isLoading={transferReasonsStore.loading}
              enableSearch
              label={t('form.placeholders.purpose')}
              items={props.reasonOptions}
              nullValue={{ isEnable: true, text: t('form.placeholders.purpose') }}
              required
              value={value}
              onChange={onChange}
            />
            <ErrorMessage text={error?.message} />
          </div>
        )}
      />
      {/* TODO: Temporarily hidden */}
      {/* {(recipient
        ? recipient?.currency === FastPaymentCurrencyEnum.USD && recipient?.bankCountryCode === FastPaymentCountryEnum.US
        : currency === FastPaymentCurrencyEnum.USD && bankCountry === FastPaymentCountryEnum.US) && (
        <Controller
          name='fastPayment'
          control={control}
          render={({ field: { value, onChange } }) => (
            <div className='flex flex-col my-2'>
              <Checkbox
                labelClassName='!font-semibold !text-soft-black'
                label={t('form.placeholders.fastPayment')}
                readOnly
                value={value}
                onChange={onChange}
              />
            </div>
          )}
        />
      )} */}
      {/* {feeStore.data !== undefined && feeStore.data !== null && !feeStore.loading && amountVal! > 0 && (
        <div className='w-full max-w-72'>
          <Text className='t-sm font-normal text-slate opacity-30 dark:text-white'>{t('form.fields.fee')}</Text>
          <div className='text-sm font-semibold text-soft-black dark:text-white'>
            {AmountUtils.transformToAmountWithCurrency(feeStore.data, props.account ? props.account.currency : currency)}
          </div>
        </div>
      )} */}
      {fxRateStore.error ? null : (
        <>
          {(recipient?.currency ||
            props.accountTo?.currency ||
            (props.account?.currency !== undefined && currency !== undefined && bankCountry !== undefined)) &&
            fxRateStore.data && (
              <div className='w-full max-w-72'>
                <div className='t-sm font-normal text-slate opacity-30 dark:text-white'>{t('form.fields.exchangeRate')}</div>
                {fxRateStore.loading ? (
                  <div className='text-sm font-semibold text-soft-black dark:text-white'>{t('form.fields.calculating')}</div>
                ) : (
                  <div className='flex items-center gap-2 text-sm font-semibold text-soft-black dark:text-white'>
                    {AmountUtils.transformToAmountWithCurrency(1, props.account?.currency)} ={' '}
                    {AmountUtils.transformToAmountWithCurrency(
                      fxRateStore.data,
                      props.accountTo?.currency || recipient?.currency || currency || props?.account?.currency,
                    )}
                    <ExchangeRate />
                  </div>
                )}
              </div>
            )}
        </>
      )}
    </div>
  );
};
